'use client';

import {
  FragmentType,
  useFragment as getFragment,
} from '$contentful-gql/index';
import { PageContext } from '$context/index';
import { NewsCardProvider } from '$providers/NewsCardProvider';
import { newsFragment } from '$providers/common/fragments';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useContext, useState } from 'react';

import { ListContent } from './';

const queryLimit = 6;

type QueryFnProps = { filter?: string; source?: string; locale?: string };
type QueryFnPageParam = { skip?: number; limit?: number; total?: number };
type QueryFnPageResponse = {
  items: FragmentType<typeof newsFragment>[];
  pageParam: QueryFnPageParam;
};

async function fetchItems(
  pageParam: QueryFnPageParam,
  props: QueryFnProps,
): Promise<QueryFnPageResponse> {
  const { skip, limit } = pageParam;
  const { locale, source, filter } = props;
  const ret = await fetch(
    `/api/list?source=${source}&filter=${filter}&limit=${limit}&skip=${skip}&locale=${locale}`,
  );
  const json = await ret.json();
  return { items: json.items, pageParam: { ...json, items: null } };
}

export function List(props: { source }) {
  const { source = 'newsroom' } = props;
  const { locale } = useContext(PageContext);
  const [filter, setFilter] = useState<'all' | 'press' | 'news'>('all');
  const { fetchNextPage, data, hasNextPage, isSuccess } = useInfiniteQuery({
    queryKey: [source, locale, filter],
    queryFn: ({
      pageParam = { skip: 0, limit: queryLimit },
    }: {
      pageParam?: QueryFnPageResponse['pageParam'];
    }) => fetchItems(pageParam, { filter, source, locale }),
    getNextPageParam: ({ pageParam }) => {
      const { skip, limit, total } = pageParam;
      const pagesLeft = total - (skip + limit);
      if (pagesLeft <= 0) return undefined;
      return { skip: skip + queryLimit, limit: queryLimit };
    },
  });

  const more = () => {
    fetchNextPage();
  };

  const allItems = isSuccess ? data.pages.flatMap((page) => page.items) : [];

  const nextProps = {
    source,
    more,
    hasNextPage,
    setFilter: (f) => setFilter(f),
  };

  /**
   * We can't rely on NewsCardProvider being null as it's treated as being
   * a React element, which can create a blank a entry if a News Page does not link to the item
   */
  function doesPageExistForItem(item: FragmentType<typeof newsFragment>) {
    const fields = getFragment(newsFragment, item);
    return (
      (fields.type === 'news' && fields.linkedFrom.pageCollection.total > 0) ||
      fields.type === 'press'
    );
  }

  return (
    <ListContent {...nextProps}>
      {allItems
        .filter((item) => doesPageExistForItem(item))
        .map((item, i) => {
          return <NewsCardProvider itemIndex={i % 3} item={item} key={i} />;
        })}
    </ListContent>
  );
}
